
/**
* UserPanelSuperAdmin
* --------------------
* This component renders the Super Admin contacts panel.
* It supports:
* - Listing master contacts with pagination & infinite scroll
* - Filtering, sorting, searching
* - Bulk actions (delete, transfer owner, bulk edit)
* - Importing contacts, call logs, notes, and tasks
* - Exporting selected contacts with notes
*
* Tech stack:
* - React + TypeScript
* - Redux for global state
* - Axios for API calls
* - Firebase Firestore for notes
* - MUI for responsiveness
*/
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import ApiTopBar from "../../Components/TopBar/ApiTopBar";
import commonStyle from "../common.module.css";
import { connect, useDispatch } from "react-redux";
import OwnerModal from "../../Components/Modals/ChangeOwnerModal/ChangeOwnerModal";
import { datesField, booleanObject, ContactColumnsMaster } from "../../Values/tables";
import Firebase from "firebase/app";
import styles from './UserPanel.module.css'
import Loading from "../../Components/Loading/Loading";
import ApiCustomTable from "../../Components/CustomTable/ApiCustomTable";
import BulkEditModal from "../../Components/Modals/BulkEditModal/BulkEditModal";
import ImportCallLogsModal from "../../Components/Modals/ImportModal/ImportCallLogsModal";
import ImportNotesModal from "../../Components/Modals/ImportModal/ImportNotesModal";
import ImportTasksModal from "../../Components/Modals/ImportModal/ImportTasksModal";
import axios from "axios";
import moment from "moment";
import { url } from "../../Values/constants";
import { setFilterObject, setRefreshContacts, showSnackbarAction, updateFilterStatus, setDeleteLeadsStatus, organizationIdWithName } from "../../Redux/actions";
import MasterContactModal from "../../Components/Modals/ImportModal/MasterContactModal";
import DeleteSelectedModal from "../../Components/Modals/DeleteSelectedModal/DeleteSelectedModal";
import { themeColors } from "../../Components/theme";
import { useMediaQuery, useTheme } from "@mui/material";
import { conatctHeader } from "../../Values/masterHeader";
import { downloadCSVMatser, exportMasterData, fetchOrganizations } from "../../Services/organizations";

/** ----------------------
 * Component Props
 * ---------------------- */
type props = {
  history: any;
  user: any;
  contacts: {
    data: any;
  };
  organizationUsers: any;
  role: any;
  teamLeadUsers: any;
  filterObject: any;
  filterSort: any;
  refreshContacts: boolean;
  searchString: string;
  theme: any;
  authToken: any;
  clerFilterStatus: any;
  deleteLeadsStatus: any;
  organizationIdNameData: any;
};

/**
 * Shared mutable helpers
 * NOTE: These persist across renders
 */
let usersList: any[] = [];
let isFinished = false;

/**
 * UserPanelSuperAdmin Component
 */
const UserPanelReplicateAdmin: FunctionComponent<props> = ({
  history,
  user,
  contacts,
  organizationUsers,
  role,
  teamLeadUsers,
  filterObject,
  filterSort,
  refreshContacts,
  searchString,
  theme,
  authToken,
  clerFilterStatus,
  deleteLeadsStatus,
  organizationIdNameData
}) => {
  const dispatcher = useDispatch();

  /** Responsive helpers */
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const [showImportModal, setShowImportModal] = useState(false);
  const [callLogsImportModal, setCallLogsImportModal] = useState(false);
  const [notesImportModal, setNotesImportModal] = useState(false);
  const [tasksImportModal, setTasksImportModal] = useState(false);
  const [show, setShow] = useState(false);
  const [filterCount, setFilterCount] = useState<any>({});
  const [deletePopup, setDeletePopop] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [owner, setOwner] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<any[]>([]);
  const [transferStatusTrueRows, setTransferStatusTrueRows] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<any[] | undefined>(undefined);
  const [searchedItem, setsearchedItem] = useState("");
  const [columnModal, setColumnModal] = useState(false);
  const [lastPage, setLastPage] = useState(-1);
  const [allContacts, setAllContacts] = useState<any[] | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [load, setLoad] = useState(false);
  const [allLeads, setAllLeads] = useState(false);
  const [apiCallFlag, setApiCallFlag] = useState(false);
  const [totalCounts, setTotalCounts] = useState<any>();
  const [freshFilterValue, setFreshFilterValue] = useState<any>();
  const [clearFilterTable, setClearFilterTable] = useState(false);
  const [organizationData, setOrganizationData] = useState<any[] | undefined>(undefined);

  useEffect(() => {
    const unSub = fetchOrganizations((data) => setOrganizationData(data));
    return unSub;
  }, []);

  useEffect(() => {
    let organizationIdNameArr: { value: number, label: string, industryId: string }[] = [];
    if (organizationData !== undefined) {
      organizationData.filter((list: any) => list.showAnalytics === true && list.organization_name != "Optiinvest").map((list: any) => {
        organizationIdNameArr.push({
          "value": list.auth_id,
          "label": list.organization_name,
          "industryId": list.industry_id
        })
      })
    }
    dispatcher(organizationIdWithName(organizationIdNameArr));
  }, [organizationData, dispatcher]);

  /**
* Memoized column configuration
* Prevents unnecessary re-renders
*/
  const contactColumns = useMemo(
    () => ContactColumnsMaster(conatctHeader),
    [conatctHeader]
  );

  /**
* Toggle table filter reset
*/
  const handleClearFilter = () => {
    setClearFilterTable(prev => !prev);
  };

  /**
* Core API Call
* - Handles pagination
* - Applies filters & sorting
* - Normalizes date fields
*/
  const callApi = async (
    paginate?: boolean,
    localPageSize?: Number,
    localPage?: Number
  ) => {
    if (isFinished === true) {
      return;
    } else {
      setLoad(true);

      /** Build filter payload */
      const filters: { [key: string]: any[] } = {};

      let feild;

      filters["transfer_status"] = [false];

      Object.keys(filterObject).forEach((item) => {
        if (!filterObject || !filterObject[item] || filterObject[item].length === 0) return;
        if (["is_button_called", "transfer_status", "associate_status", "source_status"].includes(item)) {
          if (filterObject[item].length === 2) {
            filters[item] = [true, false];
          } else {
            filters[item] = filterObject[item][0] === "True" ? [true] : [false];
          }
        } else {
          if (item !== "contact_no") {
            filters[item] = filterObject[item];
          }
        }
      });
      if (searchString === "") {
        feild = "customer_name";
      } else {
        if (searchString.match(/^[0-9]+$/) != null) {
          feild = "contact_no";
        } else {
          feild = "customer_name";
        }
      }
      setFilterCount(filters)
      let records = localPageSize ? localPageSize : pageSize;
      const apiData = {
        page: localPage ? localPage : page + 1,
        searchString: searchString,
        sort: Object.keys(filterSort).length === 0 ? { created_at: "-1" } : filterSort,
        pageSize: localPageSize ? localPageSize : pageSize,
        filter: filters,
      };
      try {

        axios.defaults.headers.common["x-access-token"] = authToken

        const res = await axios.post(url + "/leads/maskMasterSearch", apiData);

        let data: any[] = res.data;

        const recordsNum = typeof records === "number" ? records : Number(records);

        /** Stop pagination when no more data */
        if (data.length < recordsNum && paginate) {
          isFinished = true;
          setLastPage(page);
        }

        /** Normalize lead data */
        data.forEach((lead) => {
          lead.contactId = lead.Id;
          delete lead["Id"];
          Object.keys(lead).forEach((key) => {
            if (datesField.includes(key) && lead[key] !== "") {
              if (lead[key] !== null) {
                lead[key] = Firebase.firestore.Timestamp.fromDate(
                  moment(lead[key]).toDate()
                );
              } else {
                lead[key] = "";
              }
            }
          });
        });
        if (paginate && filterData) {
          let newData = [...filterData, ...data];
          setFilterData(newData);
        } else {
          setFilterData(data);
        }
        setLoad(false);
      } catch (e) {
        setLoad(false);
      }
    }
  };

  /**
* Recursive function to create a user hierarchy list
* @param email Starting user's email
* @param users List of all users
* @param uid Boolean flag
*/
  const createUsersList = (email: string, users: any[], uid: boolean) => {
    users.map((item: any) => {
      if (usersList.includes(item)) {
        return;
      } else {
        if (item.reporting_to === email && item.status === "ACTIVE") {
          if (!usersList.includes(item)) {
            usersList.push(item);
          }
          createUsersList(item.user_email, users, uid);
        }
      }
    });
  };

  // Update transfer status for selected rows
  useEffect(() => {
    let trueStatusList: any = [];
    selectedRowsData.forEach((row) => {
      Object.keys(row).forEach((item) => {
        if (item === "transfer_status") {
          trueStatusList.push(row[item]);
        }
      });
    });
    setTransferStatusTrueRows(trueStatusList);
  }, [selectedRowsData]);

  useEffect(() => {
    if (user === undefined) {
      setFilterData([]);
      return;
    }
    isFinished = false;
    setLastPage(-1);
    if (page !== 0) {
      setPage(0);
    }
    const filterStageValue = filterObject.stage?.length === 1 && filterObject?.stage?.filter((item: any) => item === "FRESH");
    filterStageValue ? setFreshFilterValue(filterStageValue[0]) : setFreshFilterValue('');
    const isAnyKeyArrayNotEmpty = Object.keys(filterObject).some(key => filterObject[key].length > 0);
    if (isAnyKeyArrayNotEmpty) {
      if ((authToken)) {
        setApiCallFlag(true);
        dispatcher(updateFilterStatus(false));
        callApi(false, pageSize, 1);
      }
    }
    else if (clerFilterStatus && apiCallFlag) {
      callApi(false, pageSize, 1);
    }
    else {
      if ((authToken && !apiCallFlag)) {
        dispatcher(updateFilterStatus(false));
        setApiCallFlag(true);
        callApi(false, pageSize, 1);
      }
    }
  }, [user, filterObject, filterSort, authToken]);

  // Fetch contacts on search
  useEffect(() => {
    if (searchString != "") {
      callApi(false, pageSize, 1);
    }
  }, [searchString]);

  useEffect(() => {
    if (deleteLeadsStatus === true) {
      callApi(false, pageSize, 1);
      dispatcher(setDeleteLeadsStatus(false));
    }
  }, [deleteLeadsStatus]);

  // Pagination effect
  useEffect(() => {
    if (
      filterData &&
      filterData.length <= (page + 1) * pageSize &&
      isFinished === false
    ) {
      if (page === 0) {
        if (authToken) {
          callApi(false, pageSize);
        }
      } else {
        if (authToken) {
          callApi(true, pageSize);
        }
      }
    }
  }, [page, pageSize, authToken]);

  // Refresh contacts effect
  useEffect(() => {
    if (refreshContacts === true) {
      if (authToken) {
        callApi();
        getContactsFilter();
        dispatcher(setRefreshContacts(false));
        const filterStageValue = filterObject.stage?.length === 1 && filterObject?.stage?.filter((item: any) => item === "FRESH");
        filterStageValue ? setFreshFilterValue(filterStageValue[0]) : setFreshFilterValue('');
      }
    }
  }, [refreshContacts, authToken]);

  useEffect(() => {
    if (user === undefined) {
      setFilterData([]);
      return;
    }
    if (authToken) {
      contactCountApi()
    }
  }, [refreshContacts, filterCount, authToken]);

  /**
* Fetch total contact count for pagination footer
*/
  const contactCountApi = async () => {
    const { reporting_to, team, branch, ...otherFilterCount } = filterCount;
    axios.defaults.headers.common["x-access-token"] = authToken;
    const res = await axios.post(url + "/leads/MasterContactCount", {
      leadFilter: otherFilterCount,
      leadUserFilter: {
        reporting_to,
        team,
        branch
      }
    });
    setTotalCounts(res.data.total);
  };

  /**
* Fetch filter values from server
*/
  const getContactsFilter = async () => {
    axios.defaults.headers.common["x-access-token"] =
      authToken
    const res = await axios.post(url + "/leads/MasterFilterValues", {});
    if (res.data[0]) {
      dispatcher(
        setFilterObject({
          ...res.data[0],
          'lead_type': ['Data', 'Leads'],
          ...booleanObject,
        })
      );
    } else {
      dispatcher(setFilterObject({}));
    }
  };
  useEffect(() => {
    if (user === undefined) {
      return;
    }
    if (authToken) {
      getContactsFilter();
    }
  }, [user, authToken]);

  useEffect(() => {
    let contact: any[] = [];
    Object.keys(contacts.data).forEach((key) => {
      contact = [...contact, ...contacts.data[key]];
    });
    setAllContacts(contact);
  }, [contacts.data]);

  useEffect(() => {
    let mapReportingTo: { [key: string]: string } = {};
    if (organizationUsers) {
      organizationUsers.forEach((item: any) => {
        if (mapReportingTo[item.uid] === undefined) {
          mapReportingTo[item.uid] = item.reporting_to;
        }
      });
    }

    if (allContacts) {
      allContacts.forEach((item: any) => {
        if (item.uid === "") {
          item["reporting_to"] = "";
        } else {
          item["reporting_to"] = mapReportingTo[item.uid]
            ? mapReportingTo[item.uid]
            : "";
        }
      });
    }

    // eslint-disable-next-line
  }, [allContacts]);

  const setUsersList = () => {
    if (role === "Lead Manager") {
      organizationUsers.map((item: any) => {
        if (usersList.includes(item)) {
          return;
        } else {
          if (item.status === "ACTIVE") {
            usersList.push(item);
          }
        }
      });
    } else {
      createUsersList(user.user_email, organizationUsers, false);
    }
  };

  useEffect(() => {
    if (selectedRows.length > 0) {
      setOwner(true);
    }
    else if (selectedRows.length === 0) {
      setOwner(false);
    }
  }, [selectedRows]);

  const clearSelectedRowsData = () => {
    setSelectedRowsData([]);
    setSelectedRows([]);
  };

  // ================================
  // Export Selected Rows
  // ================================
  // Returns array formatted for Excel export
  const exportFile = () => {
    if (!selectedRowsData?.length) {
      dispatcher(showSnackbarAction("No rows selected!", "error"));
      return;
    }
    const exportData = exportMasterData(selectedRowsData, contactColumns);
    if (!exportData.length) {
      dispatcher(showSnackbarAction("No data available for export!", "error"));
      return;
    }
    downloadCSVMatser(exportData, "ContactsExport");
    dispatcher(showSnackbarAction("Exported successfully!", "success"));
  };

  /**
* Theme styles
*/
  const topBarStyles = { backgroundColor: theme ? themeColors.backgroundColor : themeColors.backgroundColorDark, width: "100%" }
  const parentStyles = { backgroundColor: theme ? themeColors.backgroundColor : themeColors.backgroundColorDark }

  // ================================
  // Render
  // ================================
  return (
    <>
      <div className={styles.parent} style={{ display: "block", marginTop: isMobile ? "6%" : "4%", ...parentStyles }}>
        {load === true && <Loading />}
        <div className={commonStyle.topBar} style={{ marginTop: "0px", ...topBarStyles }}>

          {/* Top Action Bar */}
          <ApiTopBar
            history={history}
            title={"Add Contacts"}
            path={"/addContacts"}
            onClick={() => {
              setShowImportModal(true);
            }}
            onChange={(val) => setsearchedItem(val)}
            owner={owner}
            deleteSelected={() => {
              setDeletePopop(true);
            }}
            changeOwner={() => {
              if (selectedRowsData.length > 200) {
                dispatcher(
                  showSnackbarAction(
                    'You can transfer a maximum of 200 records',
                    'error'
                  )
                );
              } else {
                setUsersList();
                setShow(true);
              }
            }}

            filterText={"Status"}
            setColumnModal={(data) => setColumnModal(data)}
            show={true}
            showStatusBox={true}
            setBulkEdit={() => {
              setShowBulkEdit(true);
            }}
            onCallLogsImport={() => {
              setCallLogsImportModal(true);
            }}
            onNotesImport={() => {
              setNotesImportModal(true);
            }}
            onTasksImport={() => {
              setTasksImportModal(true);
            }}
            searchedString={searchString}
            setAllLeads={(data) => setAllLeads(data)}
            transferLeads={transferStatusTrueRows}
            clearFilter={handleClearFilter}
            role="Replicate"
            onExport={exportFile}
          />
        </div>
        <div className={commonStyle.parent} style={{ minHeight: "450px" }}>

          {/* Data Table */}
          <ApiCustomTable
            tableColumns={contactColumns}
            tableRows={filterData}
            selectedRows={selectedRows}
            setSelectedRows={(data) => setSelectedRows(data)}
            tableType="Replicate Contacts"
            showColumnModal={columnModal}
            hideColumnModal={() => setColumnModal(false)}
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={(data) => setSelectedRowsData(data)}
            setPage={(val) => setPage(val)}
            setPageSize={(val) => setPageSize(val)}
            callApi={callApi}
            pageSize={pageSize}
            page={page}
            isFinished={isFinished}
            totalCount={totalCounts}
            lastPage={lastPage}
            clearFilterTable={clearFilterTable}
          />
          {show && (
            <OwnerModal
              open={show}
              close={() => {
                setShow(false);
              }}
              usersList={usersList}
              rowsSelected={selectedRowsData}
              clearSelectedRowsData={clearSelectedRowsData}
              organization_id={user.organization_id}
              freshFilterValue={freshFilterValue}

            />
          )}
          {deletePopup && (
            <DeleteSelectedModal
              data={selectedRowsData}
              clearSelectedRowsData={clearSelectedRowsData}
              open={deletePopup}
              history={history}
              close={() => {
                setDeletePopop(false);
              }}
            />
          )}
          {showImportModal && (
            <MasterContactModal
              open={showImportModal}
              close={() => setShowImportModal(false)}
              history={history}
            />
          )}
          {callLogsImportModal && (
            <ImportCallLogsModal
              open={callLogsImportModal}
              close={() => setCallLogsImportModal(false)}
              organization_id={user.organization_id}
              usersList={organizationUsers}
            />
          )}

          {notesImportModal && (
            <ImportNotesModal
              open={notesImportModal}
              close={() => setNotesImportModal(false)}
            />
          )}
          {tasksImportModal && (
            <ImportTasksModal
              open={tasksImportModal}
              close={() => setTasksImportModal(false)}
              organization_id={user.organization_id}
              usersList={organizationUsers}
              user={user}
            />
          )}
          {showBulkEdit && (
            <BulkEditModal
              open={showBulkEdit}
              organization_id={user.organization_id}
              close={() => {
                setShowBulkEdit(false);
                clearSelectedRowsData();
              }}
              rowsData={selectedRowsData}
            />
          )}
        </div>
      </div>
    </>
  );
};

// ================================
// Redux Mapping
// ================================
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    role: state.user.role,
    contacts: state.contacts,
    organizationUsers: state.organizationUsers.data,
    teamLeadUsers: state.teamLeadUsers.data,
    filterObject: state.filter,
    filterSort: state.filterSort,
    refreshContacts: state.refresh.refreshContacts,
    searchString: state.searchItem.contactsSearchString,
    theme: state.theme.isLightMode,
    authToken: state.user.authToken,
    clerFilterStatus: state.user.filterClearStatus,
    deleteLeadsStatus: state.deleteLeadsStatusData.deleteLeadsStatus,
    organizationIdNameData: state.organizationIdNameData.data,
  };
};

export default connect(mapStateToProps)(UserPanelReplicateAdmin);
